/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Modal,
  Input,
  DatePicker,
  Tag,
  Alert,
} from 'antd';
import {
  ThunderboltOutlined,
  UnlockOutlined,
  LockOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { DATE_FORMAT } from 'utils/normalizers';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO } from 'utils/constants';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import {
  abrirPeriodoCalendario,
  cerrarPeriodoCalendario,
  deletePeriodoCalendario,
  generarPeriodosCalendario,
  getFechaDeCierre,
  getFechasPropuestas,
  getPeriodoCalendario,
  getPeriodosCalendario,
  getPeriodosDeNominaDisponibles,
  patchPeriodoCalendario,
  postPeriodoCalendario,
} from 'api/nominas/calendarioDeNominas';

export const calendarioDeNominaPeriodoPermission = {
  permissionModel: 'calendariodenominaperiodo',
  catalogsModels: ['percepcion', 'periododenominadetalle'],
  tabsModels: [],
};

const COLOR_SITUACION = {
  'Por Abrir': 'default',
  Abierto: 'green',
  Cerrado: 'red',
  Reabierto: 'orange',
  Pagado: 'blue',
};

const formatoFecha = (val) => (val ? moment(val).format('DD/MM/YYYY') : '');
const aTexto = (val) => (val ? val.format(DATE_FORMAT) : null);

const CalendarioDeNominasPeriodos = ({ calendarioId, calendarioActivo, permission }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [open, setOpen] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [percepciones, setPercepciones] = useState([]);
  const [disponibles, setDisponibles] = useState([]);

  const fetchData = async () => {
    if (!calendarioId) {
      setData([]);
      return;
    }
    const _data = await getPeriodosCalendario({ calendario_de_nomina: calendarioId });
    setData(_data);
  };

  useEffect(() => {
    let mounted = true;
    if (!calendarioId) {
      setData([]);
      return undefined;
    }
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _percepciones] = await Promise.all([
        getPeriodosCalendario({ calendario_de_nomina: calendarioId }),
        getPercepciones(),
      ]);
      if (mounted) {
        setData(_data);
        setPercepciones(_percepciones?.results || _percepciones || []);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [calendarioId]);

  const limpiarSeleccion = () => {
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  const recargar = async (seleccionado = null) => {
    await fetchData();
    if (seleccionado) {
      setSelectedRowKeys([seleccionado.id]);
      setSelectedData(seleccionado);
    } else {
      limpiarSeleccion();
    }
  };

  const onClickAdd = async () => {
    setLoading(true);
    limpiarSeleccion();
    form.resetFields();
    setDisponibles(await getPeriodosDeNominaDisponibles(calendarioId));
    form.setFieldsValue({ estados_globales: ESTATUS_ACTIVO });
    setOpen(true);
    setLoading(false);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const periodo = await getPeriodoCalendario(selectedRowKeys[0]);
    if (periodo) {
      form.resetFields();
      form.setFieldsValue(formatReceived(periodo));
      setSelectedData(periodo);
      setOpen(true);
    }
    setLoading(false);
  };

  // Al cambiar la Fecha de Pago se recalcula el cierre: Pago menos los días hábiles de
  // bloqueo del calendario. El usuario puede ajustarlo después.
  const recalcularCierre = async (pago) => {
    if (!pago) return;
    const cierre = await getFechaDeCierre(aTexto(pago), calendarioId);
    if (cierre) form.setFieldsValue({ fecha_de_cierre: moment(cierre) });
  };

  // Al seleccionar un Periodo de Nómina se proponen el nombre y las cuatro fechas.
  const onChangePeriodoDeNomina = async (id) => {
    if (!id) return;
    const propuesta = await getFechasPropuestas(calendarioId, id);
    if (!propuesta) return;
    form.setFieldsValue({
      periodo: propuesta.periodo,
      fecha_de_apertura: moment(propuesta.fecha_de_apertura),
      fecha_de_cierre: moment(propuesta.fecha_de_cierre),
      fecha_entrega_tesoreria: moment(propuesta.fecha_entrega_tesoreria),
      fecha_de_pago: moment(propuesta.fecha_de_pago),
    });
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const payload = {
        calendario_de_nomina: calendarioId,
        periodo: values.periodo,
        fecha_de_apertura: aTexto(values.fecha_de_apertura),
        fecha_de_cierre: aTexto(values.fecha_de_cierre),
        fecha_entrega_tesoreria: aTexto(values.fecha_entrega_tesoreria),
        fecha_de_pago: aTexto(values.fecha_de_pago),
        percepciones_adicionales: values.percepciones_adicionales || [],
      };
      if (!selectedRowKeys.length && values.periodo_de_nomina_detalle) {
        payload.periodo_de_nomina_detalle = values.periodo_de_nomina_detalle;
      }
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchPeriodoCalendario(key, payload)
        : postPeriodoCalendario(payload));
      if (saved) {
        onCancel();
        await recargar(saved);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const deleted = await deletePeriodoCalendario(selectedRowKeys[0]);
    if (deleted) {
      setVisibleAlert(false);
      await recargar();
    }
    setLoading(false);
  };

  const onGenerar = () => {
    Modal.confirm({
      title: 'Generar Periodos',
      content: 'Se generarán los periodos a partir de los Periodos de Nómina del Ejercicio y la '
        + 'Periodicidad del calendario. Fecha de Pago: fin de cada periodo (día hábil anterior si '
        + 'es inhábil); Entrega a Tesorería: un día hábil antes; Fecha de Cierre: la Fecha de Pago '
        + 'menos los días hábiles de bloqueo del calendario. Todas las fechas pueden ajustarse.',
      okText: 'Generar',
      cancelText: 'Regresar',
      onOk: async () => {
        setLoading(true);
        const generados = await generarPeriodosCalendario(calendarioId);
        if (generados) await recargar();
        setLoading(false);
      },
    });
  };

  const onAbrirCerrar = (abrir) => {
    Modal.confirm({
      title: `${abrir ? 'Abrir' : 'Cerrar'} Periodo`,
      content: abrir
        ? `¿Desea abrir el periodo ${selectedData.periodo}? Se permitirán movimientos en las funcionalidades `
          + `cerradas hasta que se cierre de nuevo; deberá cerrarse antes de la Entrega a Tesorería (${
            formatoFecha(selectedData.fecha_entrega_tesoreria) || 'sin capturar'}).`
        : `¿Desea cerrar el periodo ${selectedData.periodo}? Las funcionalidades cerradas volverán a quedar en solo lectura.`,
      okText: abrir ? 'Abrir' : 'Cerrar',
      cancelText: 'Regresar',
      onOk: async () => {
        setLoading(true);
        const periodo = await (abrir
          ? abrirPeriodoCalendario(selectedData.id)
          : cerrarPeriodoCalendario(selectedData.id));
        if (periodo) await recargar(periodo);
        setLoading(false);
      },
    });
  };

  const opcionesPercepciones = percepciones.filter((p) => [ESTATUS_ACTIVO, ESTATUS_AUTORIZADO]
    .includes(p.estados_globales) || (selectedData.percepciones_adicionales || []).includes(p.id));

  const columns = [
    {
      titleText: 'No.', dataIndex: 'numero', key: 'numero', width: 80,
    },
    {
      titleText: 'Periodo', dataIndex: 'periodo', key: 'periodo', width: 180,
    },
    {
      titleText: 'Fecha de Apertura', dataIndex: 'fecha_de_apertura', key: 'fecha_de_apertura', width: 150, render: formatoFecha,
    },
    {
      titleText: 'Fecha de Cierre', dataIndex: 'fecha_de_cierre', key: 'fecha_de_cierre', width: 150, render: formatoFecha,
    },
    {
      titleText: 'Entrega a Tesorería', dataIndex: 'fecha_entrega_tesoreria', key: 'fecha_entrega_tesoreria', width: 160, render: formatoFecha,
    },
    {
      titleText: 'Fecha de Pago', dataIndex: 'fecha_de_pago', key: 'fecha_de_pago', width: 150, render: formatoFecha,
    },
    {
      titleText: 'Percepción Adicional', dataIndex: 'percepciones_adicionales_descripcion', key: 'percepciones_adicionales_descripcion', width: 250,
    },
    {
      titleText: 'Situación',
      dataIndex: 'situacion',
      key: 'situacion',
      width: 130,
      render: (val) => <Tag color={COLOR_SITUACION[val]}>{val}</Tag>,
    },
    {
      titleText: 'Estatus', dataIndex: 'estados_globales_descripcion', key: 'estados_globales_descripcion', width: 120,
    },
  ];

  const requiredRule = [{ required: true, message: 'El campo es requerido' }];
  const sinFechasCompletas = data.some((p) => !p.fecha_entrega_tesoreria || !p.fecha_de_pago);

  return (
    <Spin tip="Cargando..." spinning={loading}>
      {sinFechasCompletas && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Hay periodos sin Entrega a Tesorería o Fecha de Pago. Mientras no se capturen, el bloqueo de cada periodo dura hasta el día anterior a la Fecha de Apertura del siguiente."
        />
      )}
      <Table
        cols={columns}
        data={data}
        rowSelection={{ selectedRowKeys, type: 'radio' }}
        handleOnRowClick={(record) => {
          setSelectedRowKeys([record.id]);
          setSelectedData(record);
        }}
        controls={{
          onClickAdd,
          onClickEdit,
          onClickDelete: () => setVisibleAlert(true),
        }}
        disableAdd={!calendarioActivo}
        disableEdit={!calendarioActivo}
        disableDelete={!calendarioActivo}
        customActions={[
          {
            onClick: onGenerar,
            permission: { model: 'calendariodenominaperiodo', action: 'add' },
            icon: <ThunderboltOutlined />,
            text: 'Generar Periodos',
            disabledBySelection: false,
            disabled: !calendarioActivo || data.length > 0,
          },
          {
            onClick: () => onAbrirCerrar(true),
            permission: { model: 'calendariodenominaperiodo', action: 'abrir' },
            icon: <UnlockOutlined />,
            text: 'Abrir',
            disabledBySelection: true,
            disabled: !selectedData?.puede_abrirse,
            className: 'success',
          },
          {
            onClick: () => onAbrirCerrar(false),
            permission: { model: 'calendariodenominaperiodo', action: 'cerrar' },
            icon: <LockOutlined />,
            text: 'Cerrar',
            disabledBySelection: true,
            disabled: selectedData?.situacion !== 'Reabierto',
            className: 'cancel',
          },
        ]}
        permission={permission}
        allowExportPDF={false}
        rowKey="id"
      />
      <Modal
        title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Periodo`}
        open={open}
        onOk={onFinish}
        onCancel={onCancel}
        okText="Guardar"
        cancelText="Regresar"
        confirmLoading={loading}
        width={800}
        forceRender
      >
        <Form layout="vertical" form={form}>
          <Row gutter={[16, 8]}>
            {!selectedRowKeys.length && (
              <Col span={24}>
                <Form.Item
                  name="periodo_de_nomina_detalle"
                  label="Periodo de Nómina"
                  extra="Opcional. Propone el nombre del periodo y todas sus fechas."
                >
                  <Select
                    keyProp="id"
                    dataSource={disponibles}
                    render={(item) => `${item.numero_de_nomina} | ${item.periodo} | ${
                      formatoFecha(item.fecha_inicial)} - ${formatoFecha(item.fecha_final)}`}
                    onChange={onChangePeriodoDeNomina}
                  />
                </Form.Item>
              </Col>
            )}
            {!!selectedRowKeys.length && (
              <Col xs={24} sm={6}>
                <Form.Item name="numero" label="No.">
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={selectedRowKeys.length ? 18 : 24}>
              <Form.Item name="periodo" label="Periodo" rules={requiredRule}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="fecha_de_apertura" label="Fecha de Apertura" rules={requiredRule}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="fecha_de_cierre"
                label="Fecha de Cierre"
                rules={requiredRule}
                extra="Inicio del bloqueo. Se calcula al elegir la Fecha de Pago; puede ajustarse."
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="fecha_entrega_tesoreria" label="Entrega a Tesorería" rules={requiredRule}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="fecha_de_pago"
                label="Fecha de Pago"
                rules={requiredRule}
                extra="Último día del bloqueo; al día siguiente abre el siguiente periodo."
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} onChange={recalcularCierre} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="percepciones_adicionales" label="Percepción Adicional">
                <Select
                  mode="multiple"
                  keyProp="id"
                  labelProp="descripcion"
                  dataSource={opcionesPercepciones}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        open={visibleAlert}
        content={`el periodo ${selectedData?.periodo || ''}`}
        loading={loading}
      />
    </Spin>
  );
};

export default CalendarioDeNominasPeriodos;
